import { deleteCookie, getCookie, hasCookie, setCookie } from "cookies-next";
import config, { cookieOptions } from "../config";
import { AES, HmacSHA256 } from "crypto-js";
import {
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
  format,
  formatDistanceToNow,
  isAfter,
  isEqual,
  parse,
  parseISO,
  subDays,
} from "date-fns";
import TransactionType from "../enum/transaction.enum";
import { InvoiceItemType, LogHistory } from "@/types";
import {
  CreateProjectConfiguration,
  DecryptEnvResponse,
  DefaultConstantCommands,
} from "@/types/project.type";
import { toast } from "sonner";
import { axios_server } from "@/backend";
import { IPlanConfiguration, SUBSCRIPTION_PLAN_TYPE } from "@brimble/models";
import { PlanConfiguration } from "@/context/plan-config";

export type SuccessData = {
  message: string;
  route: string;
  buttonText: string;
  title: string;
};

const charset =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~";

export const sanitize = (input: string | number) => {
  return String(input).replace(/['"]/g, "");
};

export const sanitizeToLowerCase = (input: string | number) => {
  return sanitize(input).toLowerCase();
};

export const isPaidPlan = (user: any, isTeam: boolean): boolean => {
  const paidPlans = ["DEVELOPER_PLAN", "TEAM_PLAN", "DATABASE_PLAN"];

  return paidPlans.includes(user?.subscription?.plan_type as string) || isTeam;
};

export const setAuthCookies = ({
  access_token,
  refresh_token,
  user_data,
}: {
  access_token: string;
  refresh_token: string;
  user_data?: string;
}) =>
  new Promise((resolve, reject) => {
    setCookie(config.ACCESS_TOKEN, access_token, {
      ...cookieOptions,
      maxAge: 60 * 60 * 24 * 7 * 2,
    });
    setCookie(config.REFRESH_TOKEN, refresh_token, {
      ...cookieOptions,
    });

    if (hasCookie(config.ACCESS_TOKEN) && hasCookie(config.REFRESH_TOKEN)) {
      resolve({ success: true });
    } else {
      reject({ success: false });
    }
    return user_data;
  });

export const removeAuthCookies = () =>
  new Promise((resolve, reject) => {
    const access_token = getCookie(config.ACCESS_TOKEN);
    deleteCookie(config.ACCESS_TOKEN);
    deleteCookie(config.REFRESH_TOKEN);
    deleteCookie(config.USER_DATA);
    if (
      !hasCookie(config.ACCESS_TOKEN) &&
      !hasCookie(config.REFRESH_TOKEN) &&
      !hasCookie(config.USER_DATA)
    ) {
      resolve({ success: true, access_token });
    } else {
      reject({ success: false });
    }
  });

export const daysFromDate = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true }).replace(
      "about",
      "",
    );
  } catch (error) {
    return `${date}`;
  }
};

export const getDuration = (startTime: Date, endTime: Date) => {
  const ms = differenceInMilliseconds(endTime, startTime);
  const secs = differenceInSeconds(endTime, startTime);
  const mins = differenceInMinutes(endTime, startTime);
  const hrs = differenceInHours(endTime, startTime);
  const days = differenceInDays(endTime, startTime);

  if (ms < 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else if (secs < 60) {
    const remainingMilliseconds = ms % 1000;
    const formattedSeconds = (secs + remainingMilliseconds / 1000).toFixed(2);
    return `${formattedSeconds}s`;
  } else if (mins < 60) {
    const remainingSeconds = secs % 60;
    return `${mins}m ${remainingSeconds}s`;
  } else if (hrs < 24) {
    const remainingMinutes = mins % 60;
    return `${hrs}h ${remainingMinutes}m`;
  } else {
    return `${days}d`;
  }
};

export const formatDate = (timestamp: string) => {
  const timePattern = /^\d{1,2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;
  let parsedDate;

  if (timePattern.test(timestamp)) {
    const currentDate = new Date();
    parsedDate = parse(timestamp, "HH:mm:ss.SSS", currentDate);
  } else {
    parsedDate = parseISO(timestamp);
  }

  // Updated format to include month and day
  return format(parsedDate, "MMM dd, HH:mm:ss.SSS");
};

export const getArrayOfNumbers = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, i) => i + start);
};

export const ObjectToQueryString = (obj: any) => {
  const str = [];
  for (const p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};

export const log_class = (log: string) => {
  if (log?.toLowerCase()?.includes("error")) {
    return "bg-red-600 text-white";
  } else if (log?.toLowerCase()?.includes("warn")) {
    return "bg-yellow-800 text-white";
  } else {
    return "";
  }
};

const generateRandomString = (length?: number) => {
  length = length || 32;

  let result = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    result += charset.charAt(Math.floor(Math.random() * n));
  }
  return result;
};

export const generateState = (value: string): string => {
  const random = generateRandomString(32);
  const stateObject = { value, random };
  const jsonString = JSON.stringify(stateObject);

  const base64Encoded = Buffer.from(jsonString).toString("base64");
  const urlEncodedState = encodeURIComponent(base64Encoded);

  return urlEncodedState;
};

export const decryptEnv = async (
  environments: { name: string; value: string }[],
) => {
  try {
    const response = await axios_server.post<DecryptEnvResponse>(
      "/v1/decrypt",
      {
        environments,
      },
    );

    return response.data.data;
  } catch (error) {
    return environments;
  }
};

export const encrypt = (value: string, key: string): string => {
  return AES.encrypt(value, key).toString();
};

export const generateRandomBase64String = (length: number) => {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

  let values: Uint8Array;

  if (typeof window === "undefined") {
    const crypto = require("crypto");
    values = new Uint8Array(crypto.randomBytes(length));
  } else {
    values = new Uint8Array(length);
    crypto.getRandomValues(values);
  }

  let result = Array.from(values)
    .map((x) => charset[x % charset.length])
    .join("");

  while (result.length % 4 !== 0) {
    result += "=";
  }

  return result;
};

export function getWeekRange(date: Date) {
  const dayOfWeek = date.getDay();
  const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const startOfWeek = new Date(date.setDate(diff));
  const endOfWeek = new Date(date.setDate(date.getDate() + 6));
  return {
    start: startOfWeek.toISOString().split("T")[0],
    end: endOfWeek.toISOString().split("T")[0],
  };
}

export const getHoursAgo = (range: string): number => {
  switch (range) {
    case "1h":
      return 1;
    case "6h":
      return 6;
    case "12h":
      return 12;
    case "24h":
      return 24;
    case "7d":
      return 24 * 7;
    case "30d":
      return 24 * 30;
    default:
      return 1;
  }
};

export const getDaysRange = (days = 1) => {
  const currentDate = new Date();
  const startDate = subDays(currentDate, days);

  return {
    start: startDate,
    end: currentDate,
  };
};

export const getDayOfWeek = (date: string) => {
  return format(new Date(date), "EEEE");
};

export const dateDiff = (startDate: string, endDate: string) => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  // Calculate the difference in days
  return differenceInDays(end, start);
};

export const readableDate = (date: string) => {
  const dateObject = parseISO(date.trim());

  return format(dateObject, "MMMM dd, yyyy");
};

export const createHeaderToken = (
  data: any,
  expiryInSeconds: number,
): string => {
  const expiryTimestamp = Date.now() + expiryInSeconds * 1000;

  const payload = {
    data,
    expiry: expiryTimestamp,
  };

  const payloadJSON = JSON.stringify(payload);

  const hmac = HmacSHA256(payloadJSON, config.HMAC_SECRET_KEY).toString();

  return `${hmac}.${Buffer.from(payloadJSON).toString("base64")}`;
};

export const isTokenExpired = (
  token: string,
): { valid: boolean; expired: boolean } => {
  const [hmac, payloadBase64] = token.split(".");

  const payloadJSON = Buffer.from(payloadBase64, "base64").toString("utf8");

  const payload = JSON.parse(payloadJSON);

  const newHmac = HmacSHA256(payloadJSON, config.HMAC_SECRET_KEY).toString();

  if (newHmac !== hmac) return { valid: false, expired: false };

  const now = Math.floor(Date.now() / 1000);

  if (now > payload.expiry) return { valid: false, expired: true };

  return { valid: true, expired: false };
};

export const successMessage = (
  type: TransactionType,
  other?: string,
): SuccessData | undefined => {
  switch (type) {
    case TransactionType.Subscription:
      return {
        message:
          "Payment successful, you can now create and manage your projects",
        route: `/settings?tab=invoice`,
        buttonText: "View Invoice",
        title: "Subscription Started Successfully",
      };
    case TransactionType.Domain:
      return {
        message:
          "Congratulations on securing your domain name, you can also manage your domain DNS records",
        route: `/domains/dns/${other}`,
        buttonText: "Manage DNS",
        title: "Domain Purchased Successfully",
      };
    case TransactionType.Card:
      return {
        message:
          "Congratulations on adding your card, you can now purchase domains and manage your subscriptions",
        route: `/settings?tab=billing`,
        buttonText: "Manage Cards",
        title: "Card Added Successfully",
      };
  }
};

const cardTypeImageMap: Record<string, string> = {
  visa: config.VISA_IMAGE,
  mastercard: config.MASTERCARD_IMAGE,
  verve: config.VERVE_IMAGE,
};

export const getCardImageUrl = (cardType: string): string => {
  const type = cardType.toLowerCase().trim();
  return (
    cardTypeImageMap[type] ||
    "https://res.cloudinary.com/dgqfojhx4/image/upload/v1709355690/icons8-credit-card-50_wckyru.png"
  );
};

export const formatBandwidth = <T extends boolean>(
  bytes: number,
  withLabel: T,
): string | number => {
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  const TB = GB * 1024;

  let value;

  if (bytes < KB) {
    value = `${bytes.toFixed(2)} ${withLabel ? "bytes" : ""}`;
  } else if (bytes < MB) {
    value = `${(bytes / KB).toFixed(2)} ${withLabel ? "KB" : ""}`;
  } else if (bytes < GB) {
    value = `${(bytes / MB).toFixed(2)} ${withLabel ? "MB" : ""}`;
  } else if (bytes < TB) {
    value = `${(bytes / GB).toFixed(2)} ${withLabel ? "GB" : ""}`;
  } else {
    value = `${(bytes / TB).toFixed(2)} ${withLabel ? "TB" : ""}`;
  }

  return withLabel ? value : Number(value);
};

export const transformInvoiceItemToItems = (
  invoiceItem: InvoiceItemType,
): { name: string; price: string }[] => {
  return Object.entries(invoiceItem)
    .filter(
      ([key, value]) =>
        key !== "description" && value !== null && typeof value === "number",
    )
    .map(([key, value]) => {
      const name = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

      return { name, price: `$${Number(value).toFixed(2)}` };
    });
};

export const getLatestActiveLog = (logs: LogHistory[]) => {
  const activeLogs = logs.filter((log: any) => log.status === "ACTIVE");

  if (activeLogs.length === 0) {
    return null;
  }

  return activeLogs.reduce((latestLog: any, currentLog: any) => {
    const latestLogTime = parseISO(latestLog.startTime);
    const currentLogTime = parseISO(currentLog.startTime);

    return isAfter(currentLogTime, latestLogTime) ? currentLog : latestLog;
  });
};

export const splitLogMessage = (log: string) => {
  const regex = /^([\d-]{4}-[\d-]{2}-[\d-]{2}T[\d:.]{8,}Z)(.*)$/;

  const match = log.match(regex);

  if (match) {
    const timestamp = match[1].trim();
    const logMessage = match[2].trim();

    return { timestamp, logMessage };
  }

  return null;
};

export const isDateSearchable = (
  startTime: string,
  endTime: string,
  lastDays: string,
): boolean => {
  const start = parseISO(startTime);
  const end = parseISO(endTime);

  const defaultStart =
    lastDays !== "-" ? subDays(new Date(), Number(lastDays)) : new Date();

  const isStartDifferent = !isEqual(start, defaultStart);

  const isEndDifferent = !isEqual(end, new Date());

  return isStartDifferent || isEndDifferent;
};

export const validateEmail = (value: string) => {
  let error = "";
  if (!value) {
    error = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(value)) {
    error = "Email address is invalid";
  }

  return error;
};

export const BuildDeploymentPayload = (
  formData: CreateProjectConfiguration,
  constants: DefaultConstantCommands,
  isPaid: boolean,
): CreateProjectConfiguration | any => {
  const data = Object.assign({}, formData);

  const {
    installCommand,
    buildCommand,
    startCommand,
    outputDirectory,
    accountType,
    user,
    isTeam,
  } = constants;

  if (
    accountType === "Organization" &&
    !data.teamId &&
    user?.subscription?.plan_type !== SUBSCRIPTION_PLAN_TYPE.DeveloperPlan
  ) {
    return toast.error("Please select a team or create one to deploy to");
  }

  if (
    data.environments.length === 1 &&
    data.environments[0].name === "" &&
    data.environments[0].value === ""
  ) {
    data.environments = [];
  }

  if (data.git) {
    data.git = data.git.toString().toUpperCase();
  }

  if (data.repo.git) {
    data.repo.git = data.repo.git.toString().toUpperCase();
  }

  if (data.rootDirectory === "./") {
    data.rootDirectory = "";
  }

  if (data.branch) {
    data.repo.branch = data.branch;
  }

  data.installCommand = data.installCommand || installCommand || "";
  data.buildCommand = data.buildCommand || buildCommand || "";
  data.startCommand = data.startCommand || startCommand || "";
  data.outputDirectory = data.outputDirectory || outputDirectory || "";

  if (!(isTeam || accountType === "Organization") || !data.teamId) {
    delete data.teamId;
  }

  const result = Object.assign({}, data, {
    ["configurations.region"]: data.configurations?.region?._id,
  });

  if (isPaid) {
    if (data.volumeMount) {
      result.volumeMount = data.volumeMount;
    }

    if (data.diskSize) {
      result.diskSize = data.diskSize;
    }
  }

  return result as CreateProjectConfiguration;
};

export const calculateCost = ({
  cpu = 0,
  memory = 0,
  storage = 0,
  defaultConfiguration,
}: {
  memory: number;
  storage: number;
  cpu: number;
  defaultConfiguration: PlanConfiguration;
}) => {
  const additionalCost =
    (Math.max(0, memory - defaultConfiguration?.memory) +
      Math.max(0, cpu - defaultConfiguration?.cpu) +
      Math.max(0, storage - defaultConfiguration?.storage)) *
    config.DEFAULTS.BASE_PRICE;

  return additionalCost;
};

export const isValidCIDR = (cidr: string): boolean => {
  const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

  if (!cidrPattern.test(cidr)) {
    return false;
  }

  const [ip, prefix] = cidr.split("/");
  const parts = ip.split(".");

  const isIpValid = parts.every((part) => {
    const num = parseInt(part, 10);
    return num >= 0 && num <= 255;
  });

  const prefixNum = parseInt(prefix, 10);
  const isPrefixValid = prefixNum >= 0 && prefixNum <= 32;

  return isIpValid && isPrefixValid;
};

export function formatShellScript(cmd: string) {
  if (!cmd) return "";

  let shebang = "";
  if (cmd.startsWith("#!")) {
    const shebangEnd = cmd.indexOf("\n");
    if (shebangEnd > -1) {
      shebang = cmd.substring(0, shebangEnd + 1);
      cmd = cmd.substring(shebangEnd + 1);
    }
  }

  const formatted = cmd
    .replace(/\s*&&\s*/g, " &&\n")
    .replace(/\s*;\s*/g, ";\n")
    .replace(/\s*\|\|\s*/g, " ||\n")
    .replace(/\s*\|\s*/g, " |\n")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line)
    .join("\n");

  return shebang + formatted;
}

export const getDiskPricing = () => {
  return Array.from({ length: 15 }, (_, i) => (i + 1) * 10).map((size) => ({
    title: `${size} GB ($${size * config.PERSISTENT_STORAGE_PRICE}/month)`,
    value: size,
  }));
};
